import type { ThemePalette } from './palette';
import {
  DURATION,
  EASING,
  ELEVATION,
  FONT_FAMILY,
  FONT_WEIGHT,
  LINE_HEIGHT,
  SPACING,
  TYPE_SCALE,
} from './scale';

/**
 * Token flattening to CSS custom properties.
 *
 * The TypeScript token objects are the single source of truth; the stylesheet
 * layer only consumes these variables. That keeps one definition per token
 * instead of a TS value and a CSS value that drift apart.
 *
 * Pure functions with no side effects: they return a name/value map and write
 * nothing. Applying them to a document is `frontend/src/shared/theme/apply-theme.ts`.
 */

/** `spacing.md` -> `--spacing-md`; `SURFACE_MUTED` -> `--color-surface-muted`. */
function cssName(prefix: string, key: string): string {
  return `--${prefix}-${key.toLowerCase().replaceAll('_', '-')}`;
}

/** The theme-independent scale variables. */
export function scaleCssVariables(): Readonly<Record<string, string>> {
  const variables: Record<string, string> = {};

  for (const [key, value] of Object.entries(SPACING)) {
    variables[cssName('spacing', key)] = value;
  }
  for (const [key, value] of Object.entries(TYPE_SCALE)) {
    variables[cssName('font-size', key)] = value;
  }
  for (const [key, value] of Object.entries(FONT_FAMILY)) {
    variables[cssName('font-family', key)] = value;
  }
  for (const [key, value] of Object.entries(FONT_WEIGHT)) {
    variables[cssName('font-weight', key)] = value;
  }
  for (const [key, value] of Object.entries(LINE_HEIGHT)) {
    variables[cssName('line-height', key)] = value;
  }
  for (const [key, value] of Object.entries(ELEVATION)) {
    variables[cssName('elevation', key)] = value;
  }
  for (const [key, value] of Object.entries(DURATION)) {
    variables[cssName('duration', key)] = value;
  }
  for (const [key, value] of Object.entries(EASING)) {
    variables[cssName('easing', key)] = value;
  }

  return Object.freeze(variables);
}

/**
 * The theme-dependent colour variables for one palette.
 *
 * Tone variables are emitted as roles (`--color-tone-indeterminate`), never as
 * state-named colours (`--color-unknown`). A component must go through the role,
 * which keeps the tone a hint rather than a state identity.
 */
export function paletteCssVariables(palette: ThemePalette): Readonly<Record<string, string>> {
  const variables: Record<string, string> = {
    [cssName('color', 'SURFACE')]: palette.SURFACE,
    [cssName('color', 'SURFACE_MUTED')]: palette.SURFACE_MUTED,
    [cssName('color', 'SURFACE_RAISED')]: palette.SURFACE_RAISED,
    [cssName('color', 'TEXT')]: palette.TEXT,
    [cssName('color', 'TEXT_MUTED')]: palette.TEXT_MUTED,
    [cssName('color', 'BORDER')]: palette.BORDER,
    [cssName('color', 'BORDER_STRONG')]: palette.BORDER_STRONG,
    [cssName('color', 'FOCUS_RING')]: palette.FOCUS_RING,
  };

  for (const [tone, value] of Object.entries(palette.TONE)) {
    variables[cssName('color-tone', tone)] = value;
  }

  return Object.freeze(variables);
}
